import React, { useEffect, useState } from "react";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import CustomFileInput from "../../customComponents/CustomFileInput";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import {
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplatesQuery,
  useUpdateTemplateMutation,
} from "./templatesApiSlice";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { Code, Delete, Download, Edit, LibraryBooks, Preview } from "@mui/icons-material";
import CustomWarningDialog from "../../customComponents/CustomWarningDialog";
import TemplateCodeEditorModal from "./TemplateCodeEditorModal";
import TemplatePreviewModal from "./TemplatePreviewModal";
import TemplateLibraryModal from "./TemplateLibraryModal";
import toast from "../../../helpers/toast";

const validationSchema = yup.object({
  name: yup.string().required("Required"),
  type: yup.string().required("Required"),
  // url: yup.string().required("Required"),
});

function Templates() {
  const { data: templates, loading: getTemplatesLoading } =
    useGetTemplatesQuery("all/mine");

  const [uploadFile, { isLoading: fileUploadLoading }] =
    useUploadFileMutation();

  const [createTemplate, { isLoading: createTemplateLoading }] =
    useCreateTemplateMutation();

  const [deleteTemplate, { isLoading: deleting }] = useDeleteTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateTemplateMutation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openCodeEditor, setOpenCodeEditor] = useState(false);

  const [selectedFileName, setSelectedFileName] = useState("");
  const [openLibrary, setOpenLibrary] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      type: "",
      description: "",
      file_url: "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const body = {
        name: values.name,
        type: values.type,
        description: values.description,
        file_url: values.file_url,
      };

      if (selectedTemplate && openEdit) {
        handleUpdateTemplate(body);
      } else {
        handleCreateTemplate(body);
      }
    },
  });

  const handleCreateTemplate = async (body) => {
    try {
      const response = await createTemplate(body).unwrap();
      formik.resetForm();
      setSelectedFileName("");
      setSelectedTemplate(null);
      setOpenEdit(false);
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    }
  };

  const handleUpdateTemplate = async (body) => {
    try {
      const response = await updateTemplate({
        ...body,
        id: selectedTemplate.id,
      }).unwrap();
      formik.resetForm();
      setSelectedFileName("");
      setSelectedTemplate(null);
      setOpenEdit(false);
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    }
  };

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      setSelectedFileName(file.name);

      const formData = new FormData();
      formData.append("file", file);

      const response = await uploadFile(formData).unwrap();
      if (response) {
        formik.setFieldValue("file_url", response.url);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTemplate(selectedTemplate).unwrap();
      toast.success("Template deleted successfully");
      setSelectedTemplate(null);
      setOpenWarning(false);
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    if (selectedTemplate && openEdit) {
      formik.setFieldValue("name", selectedTemplate.name);
      formik.setFieldValue("type", selectedTemplate.type);
      formik.setFieldValue("description", selectedTemplate.description);
      formik.setFieldValue("file_url", selectedTemplate.file_url);
      setSelectedFileName("template file");
    }
  }, [selectedTemplate, openEdit]);

  return (
    <>
      <TemplatePreviewModal
        open={openImageViewer}
        onClose={() => {
          setOpenImageViewer(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
      />

      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        action={handleDelete}
        item={selectedTemplate?.id}
        message="Are you sure you want to delete this template?"
      />

      <TemplateCodeEditorModal
        open={openCodeEditor}
        onClose={() => {
          setOpenCodeEditor(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
      />

      <TemplateLibraryModal
        open={openLibrary}
        onClose={() => setOpenLibrary(false)}
        targetTemplate={openEdit ? selectedTemplate : null}
        onSuccess={() => {
          if (openEdit) {
            formik.resetForm();
            setSelectedFileName("");
            setSelectedTemplate(null);
            setOpenEdit(false);
          }
        }}
      />

      <Grid container columnSpacing={3}>
        <Grid item xs={12} lg={4}>
          <StyledPaper>
            <Box className="paperHeader">
              <Typography variant="formHeading">
                {openEdit ? "Edit Template" : "Upload New Template"}
              </Typography>
            </Box>
            <Box className="paperBody">
              <form onSubmit={formik.handleSubmit}>
                <Grid container columnSpacing={1} rowSpacing={2}>
                  <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">
                        Template HTML File
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomFileInput
                          id="file"
                          name="file"
                          accept=".html"
                          placeholder="Choose HTML template file..."
                          value={formik.values.file_url || selectedFileName}
                          onChange={handleFileChange}
                          error={Boolean(formik.errors.file_url)}
                        />
                        {fileUploadLoading && <CircularProgress size={20} />}
                      </Stack>
                      <FormHelperText error>
                        {formik.touched.file && formik.errors.file}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">
                        template name
                      </Typography>
                      <StyledTextField
                        autoFocus
                        variant="outlined"
                        id="name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.name && Boolean(formik.errors.name)
                        }
                      />
                      <FormHelperText error>
                        {formik.touched.name && formik.errors.name}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">
                        description
                      </Typography>
                      <StyledTextField
                        autoFocus
                        variant="outlined"
                        id="description"
                        description="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.description &&
                          Boolean(formik.errors.description)
                        }
                      />
                      <FormHelperText error>
                        {formik.touched.description &&
                          formik.errors.description}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl error fullWidth>
                      <Typography variant="formSubHeading">
                        Template type
                      </Typography>
                      <StyledSelect
                        autoFocus
                        variant="outlined"
                        id="type"
                        name="type"
                        value={formik.values.type}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.type && Boolean(formik.errors.type)
                        }
                      >
                        <MenuItem value="score">Score</MenuItem>
                        <MenuItem value="midterm">MidTerm</MenuItem>
                        <MenuItem value="comment">Comment</MenuItem>
                        <MenuItem value="broadsheet">Broadsheet</MenuItem>
                        <MenuItem value="cumulative">Cumulative</MenuItem>
                      </StyledSelect>
                      <FormHelperText error>
                        {formik.touched.type && formik.errors.type}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} textAlign={"right"}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {openEdit && (
                        <StyledLoadingButton
                          variant="outlined"
                          color="inherit"
                          onClick={() => {
                            formik.resetForm();
                            setSelectedFileName("");
                            setSelectedTemplate(null);
                            setOpenEdit(false);
                          }}
                          type="button"
                        >
                          Cancel
                        </StyledLoadingButton>
                      )}
                      <StyledLoadingButton
                        variant="outlined"
                        startIcon={<LibraryBooks />}
                        onClick={() => setOpenLibrary(true)}
                        type="button"
                      >
                        Browse Library
                      </StyledLoadingButton>
                      <StyledLoadingButton
                        loading={createTemplateLoading || updating}
                        type="submit"
                      >
                        {openEdit ? "Update" : "Create"}
                      </StyledLoadingButton>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </StyledPaper>
        </Grid>
        <Grid item xs={12} lg={8}>
          <StyledPaper>
            {getTemplatesLoading && <LinearProgress />}
            <Box className="paperHeader">
              <Typography variant="formHeading">My Templates</Typography>
            </Box>
            <Box className="paperBody">
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Template Name</StyledTableCell>
                      <StyledTableCell>Template Type</StyledTableCell>
                      <StyledTableCell>Description</StyledTableCell>
                      <StyledTableCell align="right">edit</StyledTableCell>
                      <StyledTableCell align="right">edit content</StyledTableCell>
                      <StyledTableCell align="right">preview</StyledTableCell>
                      <StyledTableCell align="right">download</StyledTableCell>
                      <StyledTableCell align="right">delete</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates?.map((row) => (
                      <StyledTableRow key={row.id}>
                        <StyledTableCell>{row.name}</StyledTableCell>
                        <StyledTableCell>{row.type}</StyledTableCell>
                        <StyledTableCell>{row.description}</StyledTableCell>
                        <StyledTableCell align="right">
                          <SvgIcon
                            sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                            component={Edit}
                            inheritViewBox
                            onClick={() => {
                              setSelectedTemplate(row);
                              setOpenEdit(true);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <SvgIcon
                            sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                            component={Code}
                            inheritViewBox
                            onClick={() => {
                              setSelectedTemplate(row);
                              setOpenCodeEditor(true);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <SvgIcon
                            sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                            component={Preview}
                            inheritViewBox
                            onClick={() => {
                              setSelectedTemplate(row);
                              setOpenImageViewer(true);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <SvgIcon
                            sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                            component={Download}
                            inheritViewBox
                            onClick={() => {
                              window.open(
                                row.file_url,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {deleting && selectedTemplate?.id === row?.id ? (
                            <CircularProgress />
                          ) : (
                            <Delete
                              className="tableCellLink"
                              onClick={() => {
                                setSelectedTemplate(row);
                                setOpenWarning(true);
                              }}
                            />
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
    </>
  );
}

export default Templates;
